import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.ts';
import { useProjects, useTasks, useViews } from '@/hooks/useTasks.ts';
import { todayFilter, inboxFilter } from '@/lib/views.ts';
import { useShortcuts } from '@/hooks/useShortcuts.ts';

/**
 * App shell.
 *
 * The frame — sidebar and content together — caps at 1440px and centres in the
 * viewport. Previously the sidebar was pinned to the left edge and the content
 * column centred inside whatever remained, which at 1900px left a 474px gutter
 * between the two and an equal void on the right. The nav read as detached
 * because it was: nothing shared a grid with it.
 */
export function Layout({ children }: { children: React.ReactNode }) {
  const [navOpen, setNavOpen] = useState(false);
  const { data: projects } = useProjects();
  const { data: views } = useViews();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useShortcuts();

  const { data: todayTasks } = useTasks(todayFilter());
  const { data: inboxTasks } = useTasks(inboxFilter());

  async function handleLogout() {
    await api.auth.logout();
    queryClient.clear();
    navigate('/');
  }

  const nav = (
    <nav className="flex h-full flex-col gap-0.5 px-3 py-5">
      <div className="mb-5 flex items-baseline gap-2 px-2">
        <span className="t-title text-xl">TDL</span>
        <span aria-hidden="true" className="h-px flex-1 bg-line" />
      </div>

      <NavItem to="/" label="Today" count={todayTasks?.length} onNavigate={() => setNavOpen(false)} />
      <NavItem to="/upcoming" label="Upcoming" onNavigate={() => setNavOpen(false)} />
      <NavItem to="/inbox" label="Inbox" count={inboxTasks?.length} onNavigate={() => setNavOpen(false)} />
      <NavItem to="/review" label="Review" onNavigate={() => setNavOpen(false)} />

      <Divider />

      <NavItem to="/all" label="All tasks" onNavigate={() => setNavOpen(false)} />
      <NavItem to="/table" label="Table" onNavigate={() => setNavOpen(false)} />
      <NavItem to="/board" label="Board" onNavigate={() => setNavOpen(false)} />
      <NavItem to="/search" label="Search" onNavigate={() => setNavOpen(false)} />

      {(views ?? []).length > 0 && (
        <>
          <Divider />
          <p className="t-label mb-1 px-2">Saved</p>
          {(views ?? []).map((view) => (
            <NavItem
              key={view.id}
              to={view.kind === 'board' ? `/board/${view.id}` : `/view/${view.id}`}
              label={view.name}
              onNavigate={() => setNavOpen(false)}
            />
          ))}
        </>
      )}

      {(projects ?? []).length > 0 && (
        <>
          <Divider />
          <p className="t-label mb-1 px-2">Projects</p>
          {(projects ?? []).map((project) => (
            <NavItem
              key={project.id}
              to={`/project/${project.id}`}
              label={project.name}
              count={project.open_count}
              dot={project.color}
              onNavigate={() => setNavOpen(false)}
            />
          ))}
        </>
      )}

      <div className="mt-auto border-t border-line pt-3">
        <button
          type="button"
          onClick={handleLogout}
          className="w-full rounded-xs px-2 py-1.5 text-left text-xs text-ink-3 transition-colors duration-[120ms] ease-out hover:bg-hover hover:text-ink"
        >
          Log out
        </button>
      </div>
    </nav>
  );

  return (
    <div className="mx-auto flex h-full w-full max-w-[1440px]">
      <aside className="hidden w-60 shrink-0 border-r border-line bg-raised md:block">{nav}</aside>

      {navOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setNavOpen(false)}
            aria-hidden="true"
          />
          <aside className="fixed inset-y-0 left-0 z-50 w-64 border-r border-line bg-raised md:hidden">
            {nav}
          </aside>
        </>
      )}

      <div className="min-w-0 flex-1 overflow-y-auto">
        <button
          type="button"
          onClick={() => setNavOpen(true)}
          className="sticky top-0 z-30 flex w-full items-center gap-2 border-b border-line bg-page px-4 py-3 text-left text-xs text-ink-2 md:hidden"
        >
          <svg viewBox="0 0 14 12" aria-hidden="true" className="size-3.5">
            <path d="M0 1h14M0 6h14M0 11h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          Menu
        </button>
        {children}
      </div>
    </div>
  );
}

function Divider() {
  return <span aria-hidden="true" className="my-3 block h-px bg-line" />;
}

function NavItem({
  to,
  label,
  count,
  dot,
  onNavigate,
}: {
  to: string;
  label: string;
  count?: number;
  dot?: string;
  onNavigate: () => void;
}) {
  return (
    <NavLink
      to={to}
      end
      onClick={onNavigate}
      className={({ isActive }) =>
        `relative flex items-center gap-2 rounded-xs py-1.5 pr-2 pl-3 text-sm transition-colors duration-[120ms] ease-out ${
          isActive ? 'bg-hover text-ink' : 'text-ink-2 hover:bg-hover/60 hover:text-ink'
        }`
      }
    >
      {({ isActive }) => (
        <>
          {/* The active marker is a rule, not a fill — the same ruled-margin
              idea the task rows use, so nav and content share one language. */}
          <span
            aria-hidden="true"
            className={`absolute top-1/2 left-0 h-4 w-0.5 -translate-y-1/2 rounded-r-sm transition-colors duration-[120ms] ease-out ${
              isActive ? 'bg-brass' : 'bg-transparent'
            }`}
          />
          {dot && (
            <span
              aria-hidden="true"
              className="size-1.5 shrink-0 rounded-full"
              style={{ background: dot }}
            />
          )}
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {count !== undefined && count > 0 && (
            <span className="t-meta text-ink-3" data-numeric>
              {count}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
